import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIAssistant from "@/components/AIAssistant";
import CookieBanner from "@/components/CookieBanner";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Pricing from "@/pages/Pricing";
import Doctors from "@/pages/Doctors";
import Contact from "@/pages/Contact";
import Diseases from "@/pages/Diseases";
import DiseaseArticle from "@/pages/DiseaseArticle";
import ArticleImagesPreview from "@/pages/ArticleImagesPreview";
import Calculators from "@/pages/Calculators";
import PictureChooser from "@/pages/PictureChooser";
import NotFound from "@/pages/NotFound";
import LegacyRedirect from "@/components/LegacyRedirect";
import TrailingSlashRedirect from "@/components/TrailingSlashRedirect";

import { pickLang } from "@/i18n";
import { PAGE_SLUGS, LEGACY_PAGE_SLUGS, pageSlug } from "@/lib/routes";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function HideEmergentBadge() {
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById("emergent-badge");
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    remove();
    const observer = new MutationObserver(remove);
    observer.observe(document.body, { childList: true, subtree: false });
    const interval = setInterval(remove, 500);
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);
  return null;
}

/**
 * Legacy sub-slug redirect for /ru/hinnakiri/:section and /ru/haigused/:slug.
 * Preserves the dynamic segment when swapping the parent slug to its canonical
 * (translated) equivalent. Also preserves search + hash so pricing filters
 * (?show=...) and on-page anchors survive the redirect.
 */
function LegacySubRedirect({ page, targetLang }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const sub = params.section || params.slug;
  const slug = pageSlug(page, targetLang);
  return <Navigate to={`/${targetLang}/${slug}/${sub}${search}${hash}`} replace />;
}

/**
 * Legacy parent-slug redirect (e.g. /ru/hinnakiri → /ru/цены). Preserves
 * search + hash so `?show=` filters and anchors carry over to the new URL.
 */
function LegacyParentRedirect({ targetLang, newSlug }) {
  const { search, hash } = useLocation();
  return <Navigate to={`/${targetLang}/${newSlug}${search}${hash}`} replace />;
}

/**
 * `/et/...` prefix is not a real route (Estonian lives at the bare root).
 * Strip it and redirect so old links / manual guesses don't hit a 404.
 */
function EtPrefixRedirect() {
  const { pathname, search, hash } = useLocation();
  const stripped = pathname.replace(/^\/et(?=\/|$)/, "") || "/";
  return <Navigate to={`${stripped || "/"}${search}${hash}`} replace />;
}

/**
 * Build all <Route> elements for a given language using the localized slugs.
 * Returned as a flat array of <Route> so it can be spread directly inside <Routes>.
 */
function buildLangRoutes(lang) {
  const prefix = lang === "et" ? "" : `/${lang}`;
  const homePath = prefix === "" ? "/" : prefix;
  const s = (key) => `${prefix}/${pageSlug(key, lang)}`;

  return [
    <Route key={`${lang}-home`} path={homePath} element={<Home lang={lang} />} />,
    <Route key={`${lang}-services`} path={s("services")} element={<Services lang={lang} />} />,
    <Route key={`${lang}-pricing`} path={s("pricing")} element={<Pricing lang={lang} />} />,
    <Route key={`${lang}-pricing-sub`} path={`${s("pricing")}/:section`} element={<Pricing lang={lang} />} />,
    <Route key={`${lang}-diseases`} path={s("diseases")} element={<Diseases lang={lang} />} />,
    <Route key={`${lang}-diseases-sub`} path={`${s("diseases")}/:slug`} element={<DiseaseArticle lang={lang} />} />,
    <Route key={`${lang}-doctors`} path={s("doctors")} element={<Doctors lang={lang} />} />,
    <Route key={`${lang}-contact`} path={s("contact")} element={<Contact lang={lang} />} />,
    <Route key={`${lang}-calculators`} path={s("calculators")} element={<Calculators lang={lang} />} />,
  ];
}

/**
 * Legacy Estonian-slug URLs under /ru and /en → 301-style Navigate to the new canonical.
 */
function buildLegacyRoutes(lang) {
  if (lang === "et") return [];
  const routes = [];
  for (const [pageKey, legacySlug] of Object.entries(LEGACY_PAGE_SLUGS)) {
    if (legacySlug === PAGE_SLUGS[pageKey][lang]) continue; // no redirect needed

    const legacyBase = `/${lang}/${legacySlug}`;
    const newSlug = pageSlug(pageKey, lang);

    routes.push(
      <Route
        key={`${lang}-${pageKey}-legacy`}
        path={legacyBase}
        element={<LegacyParentRedirect targetLang={lang} newSlug={newSlug} />}
      />,
    );

    if (pageKey === "pricing") {
      routes.push(
        <Route
          key={`${lang}-pricing-legacy-sub`}
          path={`${legacyBase}/:section`}
          element={<LegacySubRedirect page="pricing" targetLang={lang} />}
        />,
      );
    }
    if (pageKey === "diseases") {
      routes.push(
        <Route
          key={`${lang}-diseases-legacy-sub`}
          path={`${legacyBase}/:slug`}
          element={<LegacySubRedirect page="diseases" targetLang={lang} />}
        />,
      );
    }
  }
  return routes;
}

// Module-level (built once at import time) — the route trees never change.
const ALL_ROUTES = [
  ...buildLangRoutes("et"),
  ...buildLangRoutes("ru"),
  ...buildLangRoutes("en"),
  ...buildLegacyRoutes("ru"),
  ...buildLegacyRoutes("en"),
];

function Shell() {
  const { pathname } = useLocation();
  const lang = pickLang(pathname);
  return (
    <TrailingSlashRedirect>
      <div className="min-h-screen flex flex-col" data-testid={`app-shell-${lang}`}>
        <Header lang={lang} />
        <main className="flex-1">
          <Routes>
            {ALL_ROUTES}

            {/* /et/... → strip the redundant prefix (ET lives at the root) */}
            <Route path="/et" element={<EtPrefixRedirect />} />
            <Route path="/et/*" element={<EtPrefixRedirect />} />

            {/* Dev tools (not indexed) */}
            <Route path="/pildid" element={<PictureChooser />} />
            <Route path="/artiklipildid" element={<ArticleImagesPreview />} />

            {/* Catch-all: redirect legacy WordPress URLs where possible,
                otherwise render the NotFound page (with noindex meta). */}
            <Route
              path="*"
              element={
                <LegacyRedirect>
                  <NotFound lang={lang} />
                </LegacyRedirect>
              }
            />
          </Routes>
        </main>
        <Footer lang={lang} />
        <AIAssistant key={lang} lang={lang} />
        <CookieBanner lang={lang} />
      </div>
    </TrailingSlashRedirect>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <HideEmergentBadge />
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
